package com.tovtalk.ui.screen.chat

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ChatBubbleOutline
import androidx.compose.material.icons.automirrored.filled.Comment
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.Notifications
import androidx.compose.material.icons.filled.PersonAdd
import androidx.compose.material.icons.filled.AlternateEmail
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.ActivityType
import com.tovtalk.domain.entity.ChatThread
import com.tovtalk.domain.entity.Notification
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.chat.ChatListViewModel
import com.tovtalk.presentation.viewmodel.notifications.NotificationViewModel
import com.tovtalk.ui.components.TovTalkAsyncImage
import com.tovtalk.ui.components.TovTalkAvatar
import com.tovtalk.ui.components.TovTalkEmptyState
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

@Composable
fun InboxScreen(
    onNavigateToProfile: (Int) -> Unit = {},
    onNavigateToPost: (postId: Int, post: com.tovtalk.domain.entity.Post?) -> Unit = { _, _ -> },
    onNavigateToChat: (Int, String) -> Unit = { _, _ -> },
    onNavigateToChatRequests: () -> Unit = {},
    onNavigateToStories: ((Int) -> Unit)? = null,
    notificationViewModel: NotificationViewModel = koinInject(),
    chatListViewModel: ChatListViewModel = koinInject()
) {
    val pagerState = rememberPagerState(pageCount = { 2 })
    val coroutineScope = rememberCoroutineScope()

    // Notification state
    val unifiedState by notificationViewModel.unifiedNotificationsState.collectAsState()
    val unreadCount by notificationViewModel.unreadCount.collectAsState()

    // Chat state
    val chatThreadsState by chatListViewModel.chatThreads.collectAsState()
    val chatRequestsState by chatListViewModel.chatRequests.collectAsState()

    val chatRequestsCount = remember(chatRequestsState) {
        when (val state = chatRequestsState) {
            is UiState.Success -> (state as UiState.Success<List<ChatThread>>).data.size
            else -> 0
        }
    }

    val chatUnreadCount = remember(chatThreadsState) {
        when (val state = chatThreadsState) {
            is UiState.Success -> (state as UiState.Success<List<ChatThread>>).data.sumOf { it.unreadCount }
            else -> 0
        }
    }

    LaunchedEffect(Unit) {
        notificationViewModel.loadUnifiedNotifications(refresh = true)
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(TovTalkTheme.colors.backgroundPrimary)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .statusBarsPadding()
        ) {
            // Title
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(vertical = TovTalkTheme.spacing.sm),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = "Inbox",
                    style = TovTalkTheme.typography.titleLarge,
                    color = TovTalkTheme.colors.textPrimary,
                    fontWeight = FontWeight.Bold
                )
            }

            // Tabs
            InboxTabs(
                selectedTab = pagerState.currentPage,
                activityHasUnread = unreadCount > 0,
                messagesUnreadCount = chatUnreadCount + chatRequestsCount,
                onTabSelected = { tab ->
                    coroutineScope.launch { pagerState.animateScrollToPage(tab) }
                }
            )

            // Pager
            HorizontalPager(
                state = pagerState,
                modifier = Modifier.fillMaxSize()
            ) { page ->
                when (page) {
                    0 -> ActivityTab(
                        notificationViewModel = notificationViewModel,
                        unifiedState = unifiedState,
                        onNavigateToProfile = onNavigateToProfile,
                        onNavigateToPost = onNavigateToPost,
                        onNavigateToChat = onNavigateToChat,
                        onNavigateToStories = onNavigateToStories
                    )
                    1 -> MessagesTab(
                        chatListViewModel = chatListViewModel,
                        chatThreadsState = chatThreadsState,
                        chatRequestsCount = chatRequestsCount,
                        onNavigateToChat = onNavigateToChat,
                        onNavigateToChatRequests = onNavigateToChatRequests,
                        onNavigateToStories = onNavigateToStories
                    )
                }
            }
        }
    }
}

// ==================== Tabs ====================

@Composable
private fun InboxTabs(
    selectedTab: Int,
    activityHasUnread: Boolean,
    messagesUnreadCount: Int,
    onTabSelected: (Int) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = TovTalkTheme.spacing.md)
    ) {
        // Activity tab
        Box(
            modifier = Modifier
                .weight(1f)
                .clickable { onTabSelected(0) }
                .padding(vertical = TovTalkTheme.spacing.sm),
            contentAlignment = Alignment.Center
        ) {
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Box {
                    Text(
                        text = StringProvider.get("notifications_title"),
                        style = TovTalkTheme.typography.titleSmall,
                        color = if (selectedTab == 0) TovTalkTheme.colors.textPrimary else TovTalkTheme.colors.textTertiary,
                        fontWeight = if (selectedTab == 0) FontWeight.Bold else FontWeight.Normal
                    )
                    if (activityHasUnread && selectedTab != 0) {
                        Box(
                            modifier = Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(TovTalkTheme.colors.error)
                                .align(Alignment.TopEnd)
                                .offset(x = 4.dp, y = (-2).dp)
                        )
                    }
                }
                Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xs))
                Box(
                    modifier = Modifier
                        .fillMaxWidth(0.5f)
                        .height(2.5.dp)
                        .background(
                            if (selectedTab == 0) TovTalkTheme.colors.textPrimary
                            else Color.Transparent
                        )
                )
            }
        }

        // Messages tab
        Box(
            modifier = Modifier
                .weight(1f)
                .clickable { onTabSelected(1) }
                .padding(vertical = TovTalkTheme.spacing.sm),
            contentAlignment = Alignment.Center
        ) {
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Box {
                    Text(
                        text = StringProvider.get("nav_messages"),
                        style = TovTalkTheme.typography.titleSmall,
                        color = if (selectedTab == 1) TovTalkTheme.colors.textPrimary else TovTalkTheme.colors.textTertiary,
                        fontWeight = if (selectedTab == 1) FontWeight.Bold else FontWeight.Normal
                    )
                    if (messagesUnreadCount > 0 && selectedTab != 1) {
                        Box(
                            modifier = Modifier
                                .align(Alignment.TopEnd)
                                .offset(x = 8.dp, y = (-6).dp)
                                .size(18.dp)
                                .clip(CircleShape)
                                .background(TovTalkTheme.colors.error),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = if (messagesUnreadCount > 99) "99+" else messagesUnreadCount.toString(),
                                style = TovTalkTheme.typography.labelSmall,
                                color = TovTalkTheme.colors.onBrand
                            )
                        }
                    }
                }
                Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xs))
                Box(
                    modifier = Modifier
                        .fillMaxWidth(0.5f)
                        .height(2.5.dp)
                        .background(
                            if (selectedTab == 1) TovTalkTheme.colors.textPrimary
                            else Color.Transparent
                        )
                )
            }
        }
    }

    HorizontalDivider(
        thickness = 0.5.dp,
        color = TovTalkTheme.colors.border.copy(alpha = TovTalkTheme.opacity.subtle)
    )
}

// ==================== Activity Tab ====================

@Composable
private fun ActivityTab(
    notificationViewModel: NotificationViewModel,
    unifiedState: com.tovtalk.presentation.state.PagingState<Notification>,
    onNavigateToProfile: (Int) -> Unit,
    onNavigateToPost: (postId: Int, post: com.tovtalk.domain.entity.Post?) -> Unit,
    onNavigateToChat: (Int, String) -> Unit,
    onNavigateToStories: ((Int) -> Unit)? = null
) {
    val listState = rememberLazyListState()

    val shouldLoadMore = remember {
        derivedStateOf {
            val lastVisibleItem = listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0
            unifiedState.items.isNotEmpty() &&
                lastVisibleItem >= unifiedState.items.size - 3 &&
                !unifiedState.isLoading &&
                !unifiedState.isLoadingMore &&
                unifiedState.hasMore
        }
    }

    LaunchedEffect(shouldLoadMore.value) {
        if (shouldLoadMore.value) notificationViewModel.loadMoreNotifications()
    }

    when {
        unifiedState.isLoading && unifiedState.items.isEmpty() -> {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = TovTalkTheme.colors.brand)
            }
        }
        unifiedState.items.isEmpty() && !unifiedState.isLoading -> {
            ActivityEmptyState()
        }
        else -> {
            LazyColumn(
                state = listState,
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(bottom = TovTalkTheme.spacing.md)
            ) {
                // Category icons row
                item {
                    ActivityCategoryRow(
                        notifications = unifiedState.items
                    )
                }

                // Activity items
                items(
                    items = unifiedState.items,
                    key = { it.id },
                    contentType = {
                        when (it) {
                            is Notification.Activity -> "activity"
                            is Notification.Admin -> "admin"
                        }
                    }
                ) { notification ->
                    when (notification) {
                        is Notification.Activity -> ActivityNotificationItem(
                            notification = notification,
                            onClick = {
                                if (!notification.isRead) notificationViewModel.markAsRead(notification)
                                when (notification.type) {
                                    ActivityType.FOLLOW_USER -> onNavigateToProfile(notification.fromUser.id)
                                    ActivityType.NEW_MESSAGE -> onNavigateToChat(
                                        notification.fromUser.id,
                                        notification.fromUser.fullname
                                    )
                                    else -> notification.post?.let { onNavigateToPost(it.id, it) }
                                }
                            },
                            onProfileClick = { onNavigateToProfile(notification.fromUser.id) },
                            onStoryClick = if (notification.fromUser.hasActiveStory && onNavigateToStories != null) { { onNavigateToStories(notification.fromUser.id) } } else null
                        )
                        is Notification.Admin -> AdminNotificationItem(
                            notification = notification,
                            onClick = {
                                if (!notification.isRead) notificationViewModel.markAsRead(notification)
                            }
                        )
                    }
                }

                if (unifiedState.isLoadingMore) {
                    item {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(TovTalkTheme.spacing.md),
                            contentAlignment = Alignment.Center
                        ) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(TovTalkTheme.sizes.iconMd),
                                color = TovTalkTheme.colors.brand
                            )
                        }
                    }
                }
            }
        }
    }
}

// ==================== Category Icons Row ====================

@Composable
private fun ActivityCategoryRow(
    notifications: List<Notification>
) {
    val adminCount = notifications.count { it is Notification.Admin && !it.isRead }
    val followCount = notifications.count {
        it is Notification.Activity && it.type == ActivityType.FOLLOW_USER && !it.isRead
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = TovTalkTheme.spacing.md, vertical = TovTalkTheme.spacing.md),
        horizontalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.lg)
    ) {
        CategoryIcon(
            icon = Icons.Default.Notifications,
            label = StringProvider.get("notifications_filter_admin"),
            unreadCount = adminCount
        )
        CategoryIcon(
            icon = Icons.Default.PersonAdd,
            label = StringProvider.get("notifications_filter_follows"),
            unreadCount = followCount
        )
    }

    HorizontalDivider(
        thickness = 0.5.dp,
        color = TovTalkTheme.colors.border.copy(alpha = TovTalkTheme.opacity.moderate)
    )
}

@Composable
private fun CategoryIcon(
    icon: ImageVector,
    label: String,
    unreadCount: Int
) {
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Box {
            Box(
                modifier = Modifier
                    .size(52.dp)
                    .clip(CircleShape)
                    .background(TovTalkTheme.colors.surfaceOverlay),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = label,
                    tint = TovTalkTheme.colors.textPrimary,
                    modifier = Modifier.size(TovTalkTheme.sizes.iconMd)
                )
            }
            if (unreadCount > 0) {
                Box(
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .offset(x = 2.dp, y = (-2).dp)
                        .size(18.dp)
                        .clip(CircleShape)
                        .background(TovTalkTheme.colors.error),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = if (unreadCount > 99) "99+" else unreadCount.toString(),
                        style = TovTalkTheme.typography.labelSmall,
                        color = TovTalkTheme.colors.onBrand,
                        fontWeight = FontWeight.Bold
                    )
                }
            }
        }
        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xxs))
        Text(
            text = label,
            style = TovTalkTheme.typography.caption,
            color = TovTalkTheme.colors.textSecondary
        )
    }
}

// ==================== Activity Notification Item ====================

@Composable
private fun ActivityNotificationItem(
    notification: Notification.Activity,
    onClick: () -> Unit,
    onProfileClick: () -> Unit,
    onStoryClick: (() -> Unit)? = null
) {
    val bgColor by animateColorAsState(
        targetValue = if (!notification.isRead) TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.ultraFaint)
        else Color.Transparent,
        label = "activityBg"
    )
    val itemAlpha = if (notification.isRead) 0.75f else 1f

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(bgColor)
            .clickable(onClick = onClick)
            .padding(horizontal = TovTalkTheme.spacing.md, vertical = TovTalkTheme.spacing.sm)
            .alpha(itemAlpha),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Avatar with action badge
        Box(modifier = Modifier.clickable(onClick = onProfileClick)) {
            TovTalkAvatar(
                imageUrl = notification.fromUser.profilePhoto,
                size = 46.dp,
                name = notification.fromUser.fullname,
                hasStory = notification.fromUser.hasActiveStory && onStoryClick != null,
                onStoryClick = onStoryClick
            )
            // Action type badge
            Box(
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .offset(x = (-2).dp, y = 2.dp)
                    .size(20.dp)
                    .clip(CircleShape)
                    .background(getActionBadgeColor(notification.type)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = getActionBadgeIcon(notification.type),
                    contentDescription = null,
                    tint = TovTalkTheme.colors.onBrand,
                    modifier = Modifier.size(12.dp)
                )
            }
        }

        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))

        // Text content
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = buildAnnotatedString {
                    withStyle(SpanStyle(fontWeight = FontWeight.Bold)) {
                        append(notification.fromUser.username)
                    }
                    append(" ")
                    append(getActionText(notification))
                },
                style = TovTalkTheme.typography.bodyMedium,
                color = TovTalkTheme.colors.textPrimary,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis
            )
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xxxs))
            Text(
                text = formatTimeAgo(notification.createdAt),
                style = TovTalkTheme.typography.caption,
                color = TovTalkTheme.colors.textTertiary
            )
        }

        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))

        // Right side: thumbnail or follow button
        when (notification.type) {
            ActivityType.FOLLOW_USER -> {
                // Could add follow-back button here in the future
            }
            else -> {
                notification.post?.thumbnail?.let { thumbUrl ->
                    Box(
                        modifier = Modifier
                            .size(44.dp)
                            .clip(TovTalkTheme.shapes.extraSmall)
                            .background(TovTalkTheme.colors.surfaceOverlay)
                    ) {
                        TovTalkAsyncImage(
                            url = thumbUrl,
                            modifier = Modifier.fillMaxSize(),
                            clipToCircle = false,
                            contentScale = ContentScale.Crop
                        )
                    }
                }
            }
        }
    }
}

// ==================== Admin Notification Item ====================

@Composable
private fun AdminNotificationItem(
    notification: Notification.Admin,
    onClick: () -> Unit
) {
    val bgColor by animateColorAsState(
        targetValue = if (!notification.isRead) TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.ultraFaint)
        else Color.Transparent,
        label = "adminBg"
    )

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(bgColor)
            .clickable(onClick = onClick)
            .padding(horizontal = TovTalkTheme.spacing.md, vertical = TovTalkTheme.spacing.sm),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // System icon
        Box {
            Box(
                modifier = Modifier
                    .size(46.dp)
                    .clip(CircleShape)
                    .background(TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.soft)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    Icons.Default.Notifications,
                    contentDescription = null,
                    modifier = Modifier.size(TovTalkTheme.sizes.iconMd),
                    tint = TovTalkTheme.colors.brand
                )
            }
        }

        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = notification.title,
                style = TovTalkTheme.typography.titleSmall,
                color = TovTalkTheme.colors.textPrimary,
                fontWeight = FontWeight.Bold
            )
            Text(
                text = notification.message,
                style = TovTalkTheme.typography.bodySmall,
                color = TovTalkTheme.colors.textSecondary,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis
            )
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xxxs))
            Text(
                text = formatTimeAgo(notification.createdAt),
                style = TovTalkTheme.typography.caption,
                color = TovTalkTheme.colors.textTertiary
            )
        }
    }
}

// ==================== Messages Tab ====================

@Composable
private fun MessagesTab(
    chatListViewModel: ChatListViewModel,
    chatThreadsState: UiState<List<ChatThread>>,
    chatRequestsCount: Int,
    onNavigateToChat: (Int, String) -> Unit,
    onNavigateToChatRequests: () -> Unit,
    onNavigateToStories: ((Int) -> Unit)? = null
) {
    when (chatThreadsState) {
        is UiState.Loading -> {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = TovTalkTheme.colors.brand)
            }
        }
        is UiState.Error -> {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = (chatThreadsState as UiState.Error).message,
                    color = TovTalkTheme.colors.error
                )
            }
        }
        is UiState.Success -> {
            val threads = (chatThreadsState as UiState.Success<List<ChatThread>>).data
            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(bottom = TovTalkTheme.spacing.md)
            ) {
                // Chat requests banner
                if (chatRequestsCount > 0) {
                    item {
                        ChatRequestsBanner(
                            count = chatRequestsCount,
                            onClick = onNavigateToChatRequests
                        )
                    }
                }

                if (threads.isEmpty()) {
                    item {
                        TovTalkEmptyState(
                            message = StringProvider.get("chat_empty_inbox"),
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(400.dp)
                        )
                    }
                } else {
                    items(threads, key = { it.otherUser.id }) { thread ->
                        ChatThreadItem(
                            thread = thread,
                            onClick = { onNavigateToChat(thread.otherUser.id, thread.otherUser.fullname) },
                            onStoryClick = if (thread.otherUser.hasActiveStory && onNavigateToStories != null) { { onNavigateToStories(thread.otherUser.id) } } else null
                        )
                    }
                }
            }
        }
        else -> {}
    }
}

// ==================== Chat Requests Banner ====================

@Composable
private fun ChatRequestsBanner(
    count: Int,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .background(TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.ultraFaint))
            .padding(horizontal = TovTalkTheme.spacing.md, vertical = TovTalkTheme.spacing.sm),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box {
            Box(
                modifier = Modifier
                    .size(46.dp)
                    .clip(CircleShape)
                    .background(TovTalkTheme.colors.surfaceOverlay),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    Icons.Default.ChatBubbleOutline,
                    contentDescription = null,
                    tint = TovTalkTheme.colors.textPrimary,
                    modifier = Modifier.size(TovTalkTheme.sizes.iconMd)
                )
            }
            Box(
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .offset(x = 2.dp, y = (-2).dp)
                    .size(18.dp)
                    .clip(CircleShape)
                    .background(TovTalkTheme.colors.error),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = count.toString(),
                    style = TovTalkTheme.typography.labelSmall,
                    color = TovTalkTheme.colors.onBrand,
                    fontWeight = FontWeight.Bold
                )
            }
        }
        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = StringProvider.get("chat_requests_title"),
                style = TovTalkTheme.typography.titleSmall,
                color = TovTalkTheme.colors.textPrimary,
                fontWeight = FontWeight.SemiBold
            )
            Text(
                text = "$count ${StringProvider.get("chat_requests_title")}",
                style = TovTalkTheme.typography.bodySmall,
                color = TovTalkTheme.colors.textSecondary
            )
        }
    }

    HorizontalDivider(
        thickness = 0.5.dp,
        color = TovTalkTheme.colors.border.copy(alpha = TovTalkTheme.opacity.moderate)
    )
}

// ==================== Chat Thread Item ====================

@Composable
private fun ChatThreadItem(
    thread: ChatThread,
    onClick: () -> Unit,
    onStoryClick: (() -> Unit)? = null
) {
    val hasUnread = thread.unreadCount > 0
    val backgroundColor by animateColorAsState(
        targetValue = if (hasUnread) TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.faintest)
        else Color.Transparent,
        label = "threadBg"
    )

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(backgroundColor)
            .clickable(onClick = onClick)
            .padding(horizontal = TovTalkTheme.spacing.md, vertical = TovTalkTheme.spacing.sm),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TovTalkAvatar(
            imageUrl = thread.otherUser.profilePhoto,
            size = 50.dp,
            name = thread.otherUser.fullname,
            hasStory = thread.otherUser.hasActiveStory && onStoryClick != null,
            onStoryClick = onStoryClick
        )
        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
        Column(modifier = Modifier.weight(1f)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = thread.otherUser.fullname,
                    style = TovTalkTheme.typography.titleSmall,
                    color = TovTalkTheme.colors.textPrimary,
                    fontWeight = if (hasUnread) FontWeight.Bold else FontWeight.Normal
                )
                thread.lastMessage?.let { lastMsg ->
                    Text(
                        text = formatMessageTime(lastMsg.createdAt),
                        style = TovTalkTheme.typography.caption,
                        color = if (hasUnread) TovTalkTheme.colors.brand else TovTalkTheme.colors.textTertiary
                    )
                }
            }
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xxxs))
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                thread.lastMessage?.let { lastMsg ->
                    Text(
                        text = lastMsg.content ?: "",
                        style = TovTalkTheme.typography.bodySmall,
                        color = if (hasUnread) TovTalkTheme.colors.textPrimary else TovTalkTheme.colors.textSecondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        fontWeight = if (hasUnread) FontWeight.Medium else FontWeight.Normal,
                        modifier = Modifier.weight(1f)
                    )
                }
                if (hasUnread) {
                    Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xs))
                    Box(
                        modifier = Modifier
                            .size(8.dp)
                            .clip(CircleShape)
                            .background(TovTalkTheme.colors.brand)
                    )
                }
            }
        }
    }
}

// ==================== Empty State ====================

@Composable
private fun ActivityEmptyState() {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(TovTalkTheme.spacing.xl),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(
            imageVector = Icons.Default.Notifications,
            contentDescription = null,
            modifier = Modifier.size(TovTalkTheme.sizes.emptyStateIconSize),
            tint = TovTalkTheme.colors.textTertiary
        )
        Spacer(modifier = Modifier.size(TovTalkTheme.spacing.md))
        Text(
            text = StringProvider.get("notifications_empty_title"),
            style = TovTalkTheme.typography.titleMedium,
            color = TovTalkTheme.colors.textPrimary
        )
        Spacer(modifier = Modifier.size(TovTalkTheme.spacing.xs))
        Text(
            text = StringProvider.get("notifications_empty_desc"),
            style = TovTalkTheme.typography.bodyMedium,
            color = TovTalkTheme.colors.textSecondary
        )
    }
}

// ==================== Helpers ====================

private fun getActionBadgeIcon(type: ActivityType): ImageVector = when (type) {
    ActivityType.LIKE_POST, ActivityType.LIKE_COMMENT -> Icons.Default.Favorite
    ActivityType.COMMENT_POST, ActivityType.REPLY_COMMENT -> Icons.AutoMirrored.Filled.Comment
    ActivityType.FOLLOW_USER -> Icons.Default.PersonAdd
    ActivityType.MENTION_POST, ActivityType.MENTION_COMMENT -> Icons.Default.AlternateEmail
    ActivityType.NEW_MESSAGE -> Icons.Default.ChatBubbleOutline
}

@Composable
private fun getActionBadgeColor(type: ActivityType): Color = when (type) {
    ActivityType.LIKE_POST, ActivityType.LIKE_COMMENT -> TovTalkTheme.colors.error
    ActivityType.COMMENT_POST, ActivityType.REPLY_COMMENT -> TovTalkTheme.colors.brand
    ActivityType.FOLLOW_USER -> TovTalkTheme.colors.success
    ActivityType.MENTION_POST, ActivityType.MENTION_COMMENT -> TovTalkTheme.colors.warning
    ActivityType.NEW_MESSAGE -> TovTalkTheme.colors.brand
}

private fun getActionText(notification: Notification.Activity): String {
    val from = notification.fromUser.username
    return when (notification.type) {
        ActivityType.LIKE_POST -> StringProvider.get("notification_liked_post", from).removePrefix("$from ")
        ActivityType.LIKE_COMMENT -> StringProvider.get("notification_liked_comment", from).removePrefix("$from ")
        ActivityType.COMMENT_POST -> {
            val base = StringProvider.get("notification_commented", from).removePrefix("$from ")
            val commentPreview = notification.comment?.text
            if (commentPreview != null) "$base: \"$commentPreview\"" else base
        }
        ActivityType.FOLLOW_USER -> StringProvider.get("notification_followed", from).removePrefix("$from ")
        ActivityType.NEW_MESSAGE -> StringProvider.get("notification_new_message", from).removePrefix("$from ")
        ActivityType.MENTION_POST -> StringProvider.get("notification_mentioned_post", from).removePrefix("$from ")
        ActivityType.MENTION_COMMENT -> StringProvider.get("notification_mentioned_comment", from).removePrefix("$from ")
        ActivityType.REPLY_COMMENT -> StringProvider.get("notification_replied", from).removePrefix("$from ")
    }
}

private fun formatTimeAgo(instant: Instant): String {
    val now = com.tovtalk.utils.nowInstant()
    val diffMs = now.toEpochMilliseconds() - instant.toEpochMilliseconds()
    val diffMin = diffMs / 60_000
    val diffHours = diffMs / 3600_000
    val diffDays = diffMs / 86400_000
    return when {
        diffMin < 1 -> StringProvider.get("time_now")
        diffMin < 60 -> StringProvider.get("time_minutes", diffMin.toInt())
        diffHours < 24 -> StringProvider.get("time_hours", diffHours.toInt())
        diffDays < 7 -> StringProvider.get("time_days", diffDays.toInt())
        else -> {
            val localDateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
            "${localDateTime.day}/${localDateTime.month.ordinal + 1}"
        }
    }
}

private fun formatMessageTime(instant: Instant): String {
    val now = com.tovtalk.utils.nowInstant()
    val diffMs = now.toEpochMilliseconds() - instant.toEpochMilliseconds()
    val diffMin = diffMs / 60_000
    val diffHours = diffMs / 3600_000
    val diffDays = diffMs / 86400_000
    return when {
        diffMin < 1 -> "now"
        diffMin < 60 -> "${diffMin}m"
        diffHours < 24 -> "${diffHours}h"
        diffDays < 7 -> "${diffDays}d"
        else -> {
            val localDateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
            "${localDateTime.day}/${localDateTime.month.ordinal + 1}"
        }
    }
}
